import { BADGES } from "../../data/badges";
import type { BadgeGroup, BadgeId } from "../../types";
import { badgeArt, lockedBadgeArt } from "../badgeArt";
import { bind } from "../dom";
import { esc } from "../helpers";
import { openShareModal } from "../modals";
import { buildBadgesImage } from "../share";
import { t } from "../i18n";
import type { Screen, ScreenContext } from "./context";

/** The emblem collection, grouped by section with earned/total tallies. */
export function renderBadges(ctx: ScreenContext): Screen {
  const T = t();
  const unlockedIds = ctx.state.unlockedBadges;
  const unlocked = (id: BadgeId): boolean => unlockedIds.includes(id);
  const renderBadge = (b: (typeof BADGES)[number]): string => {
    const earned = unlocked(b.id);
    // Un-earned badges show the shared locked medallion, not their own art.
    const art = earned ? badgeArt(b.id) : lockedBadgeArt;
    const icon = art
      ? `<img class="badgeArt" src="${art}" alt="" width="56" height="56" />`
      : `<div class="badgeIcon">${earned ? "🏅" : "🔒"}</div>`;
    return `<div class="badge ${earned ? "unlocked" : "locked"}">
        ${icon}
        <div>
          <div class="badgeName">${esc(T.badgeName(b.id))}</div>
          <div class="badgeDesc">${esc(T.badgeDesc(b.id))}</div>
        </div>
      </div>`;
  };
  // One section per group, in a fixed display order. Each shows an earned/total
  // tally so a long catalog stays scannable as more badges are added.
  const groupOrder: readonly BadgeGroup[] = ["championship", "mastery", "nation"];
  const sections = groupOrder
    .map((group) => {
      const inGroup = BADGES.filter((b) => b.group === group);
      const earned = inGroup.filter((b) => unlocked(b.id)).length;
      const items = inGroup.map(renderBadge).join("");
      return `<div class="badgeSection">
          <div class="badgeSectionHead">
            <h3 class="badgeSectionTitle">${esc(T.badgeGroupName(group))}</h3>
            <span class="badgeSectionCount">${earned}/${inGroup.length}</span>
          </div>
          <div class="badgeGrid">${items}</div>
        </div>`;
    })
    .join("");
  const html = `<div class="fade">
      <button class="btn ghost sm" id="backTop" style="margin-bottom:12px">← ${T.back}</button>
      <h2>${T.badgesTitle}</h2>
      <p class="sub">${T.badgesSub}</p>
      ${sections}
      <div style="margin-top:18px;display:flex;gap:10px;flex-wrap:wrap">
        <button class="btn" id="shareBadgesBtn">${T.shareBadges}</button>
        <button class="btn ghost" id="backBtn">${T.back}</button>
      </div>
    </div>`;
  return {
    html,
    bind() {
      bind("shareBadgesBtn", "click", () => void shareBadges(unlockedIds));
      bind("backTop", "click", ctx.actions.closeBadges);
      bind("backBtn", "click", ctx.actions.closeBadges);
    },
  };
}

// Render the emblem-collection image, then open the share popup. Mirrors
// onShare's button busy/error states.
async function shareBadges(unlocked: readonly BadgeId[]): Promise<void> {
  const T = t();
  const btn = document.getElementById("shareBadgesBtn") as HTMLButtonElement | null;
  const original = btn?.textContent ?? T.shareBadges;
  if (btn) {
    btn.disabled = true;
    btn.textContent = T.sharePreparing;
  }
  try {
    const img = await buildBadgesImage(unlocked);
    if (img) openShareModal(img, T.badgesTitle);
    else if (btn) btn.textContent = T.shareUnavailable;
  } catch {
    if (btn) btn.textContent = T.shareUnavailable;
  } finally {
    if (btn) {
      btn.disabled = false;
      if (btn.textContent === T.sharePreparing) btn.textContent = original;
    }
  }
}
